import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, CardContent,
  ToggleButtonGroup, ToggleButton, Chip, CircularProgress, Alert,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Navbar from '../components/landing/Navbar';
import { COLORS } from '../utils/colors';
import { API_URL } from '../utils/api';

type ResourceCategory = 'blog' | 'whitepaper' | 'press';

// Shape returned by GET /resources (see backend/src/handlers/resources/index.ts mapResource).
interface ApiResource {
  id: string;
  category: ResourceCategory;
  postType: 'hosted' | 'external';
  title: string;
  slug: string | null;
  description: string;
  coverImageUrl: string | null;
  externalUrl: string | null;
  displayDate: string;
}

const CATEGORY_META: Record<
  ResourceCategory,
  { label: string; icon: React.ReactNode; accent: string; pastel: string }
> = {
  blog: {
    label: 'Blog',
    icon: <ArticleOutlinedIcon sx={{ fontSize: 18 }} />,
    accent: COLORS.primary,
    pastel: COLORS.pastelTeal,
  },
  whitepaper: {
    label: 'Whitepapers',
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
    accent: COLORS.categoryDestination,
    pastel: COLORS.pastelPlum,
  },
  press: {
    label: 'Press',
    icon: <CampaignOutlinedIcon sx={{ fontSize: 18 }} />,
    accent: COLORS.categoryCommunity,
    pastel: COLORS.pastelSand,
  },
};

const ResourcesPage: React.FC = () => {
  const [filter, setFilter] = useState<ResourceCategory>('blog');
  const [resources, setResources] = useState<ApiResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    // Public endpoint — plain fetch, no auth wrapper (avoids the 401->logout path).
    (async () => {
      try {
        const res = await fetch(`${API_URL}/resources`);
        const data = await res.json();
        if (cancelled) return;
        if (data.success) {
          setResources(data.resources || []);
        } else {
          setError(data.error || 'Failed to load resources');
        }
      } catch {
        if (!cancelled) setError('Failed to load resources');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const items = resources.filter((r) => r.category === filter);
  const meta = CATEGORY_META[filter];

  // Where a card links: hosted -> on-site reader; external -> out (new tab).
  // Empty target -> not yet linkable (renders "Coming soon").
  const linkFor = (r: ApiResource): { href: string; external: boolean } | null => {
    if (r.postType === 'hosted' && r.slug) return { href: `/resources/${r.slug}`, external: false };
    if (r.postType === 'external' && r.externalUrl) return { href: r.externalUrl, external: true };
    return null;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: COLORS.paper }}>
      <Navbar />

      {/* Header — brand teal gradient, consistent with Hero / Why Percoro */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 60%, ${COLORS.primaryLight} 100%)`,
          color: '#fff',
          py: { xs: 6, md: 8 },
          px: 2,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute', width: 380, height: 380, borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)', top: -160, right: -120,
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Chip
            icon={<AutoAwesomeIcon sx={{ fontSize: 16, color: '#fff !important' }} />}
            label="Building Percoro in public"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600,
              mb: 2, '& .MuiChip-icon': { color: '#fff' },
            }}
          />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: '2rem', md: '2.75rem' } }}>
            Resources
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 400, color: 'rgba(255,255,255,0.9)', maxWidth: 640, mx: 'auto' }}>
            The build story behind Percoro — essays, deep dives, and press from a solo founder building an
            AI-native travel platform out in the open.
          </Typography>
        </Container>
      </Box>

      {/* Category filter */}
      <Container maxWidth="md" sx={{ mt: { xs: -3, md: -4 }, position: 'relative', zIndex: 2 }}>
        <Box
          sx={{
            display: 'flex', justifyContent: 'center', bgcolor: COLORS.white,
            borderRadius: 3, py: 1.5, px: 1, boxShadow: '0 8px 30px rgba(31,78,74,0.12)',
          }}
        >
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, v) => v && setFilter(v)}
            size="small"
            sx={{
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                border: 'none', borderRadius: '20px !important', textTransform: 'none',
                fontWeight: 600, color: COLORS.inkMuted, px: 2.5, mx: 0.5,
                '&.Mui-selected': {
                  bgcolor: COLORS.primary, color: '#fff',
                  '&:hover': { bgcolor: COLORS.primaryDark },
                },
              },
            }}
          >
            {(Object.keys(CATEGORY_META) as ResourceCategory[]).map((key) => (
              <ToggleButton key={key} value={key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {CATEGORY_META[key].icon}
                  {CATEGORY_META[key].label}
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Container>

      {/* Content */}
      <Container maxWidth="md" sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: COLORS.primary }} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : items.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center', py: 8, px: 3, borderRadius: 4,
              bgcolor: meta.pastel, color: meta.accent,
            }}
          >
            <Box sx={{ fontSize: 40, mb: 1, display: 'flex', justifyContent: 'center' }}>{meta.icon}</Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: COLORS.ink }}>
              {meta.label} coming soon
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.inkMuted, maxWidth: 420, mx: 'auto' }}>
              We're putting this together. Check back shortly, or follow the build on the Blog tab in the meantime.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {items.map((item) => {
              const cMeta = CATEGORY_META[item.category];
              const link = linkFor(item);
              return (
                <Card
                  key={item.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 3, borderColor: '#E6E0D6', overflow: 'hidden',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(31,78,74,0.12)' },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                      <Chip
                        icon={cMeta.icon as React.ReactElement}
                        label={cMeta.label}
                        size="small"
                        sx={{
                          bgcolor: cMeta.pastel, color: cMeta.accent, fontWeight: 600,
                          '& .MuiChip-icon': { color: cMeta.accent },
                        }}
                      />
                      {item.displayDate && (
                        <Typography variant="caption" sx={{ color: COLORS.inkMuted }}>
                          {item.displayDate}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.ink, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.inkMuted, lineHeight: 1.65, mb: 2 }}>
                      {item.description}
                    </Typography>
                    {link ? (
                      <Box
                        component="a"
                        href={link.href}
                        {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.5,
                          color: COLORS.primary, fontWeight: 600, fontSize: '0.9rem',
                          textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {link.external ? 'Read on LinkedIn' : 'Read article'}
                        {link.external && <OpenInNewIcon sx={{ fontSize: 16 }} />}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: COLORS.inkMuted, fontStyle: 'italic' }}>
                        Coming soon
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ResourcesPage;
